import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  CreateCustomerInput,
  CustomerSummary,
  MergeCustomersInput,
  UpdateCustomerInput,
} from '../../../types/api';
import { useToast } from '../../../hooks/useToast';

export function useCreateCustomer() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      apiClient.post<CustomerSummary>(endpoints.customers.create, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
    onError: () => {
      toast.error('Failed to create customer. Please try again.');
    },
  });
}

export function useUpdateCustomer(uuid: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateCustomerInput) =>
      apiClient.put<CustomerSummary>(endpoints.customers.update(uuid), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(uuid) });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
    onError: () => {
      toast.error('Failed to update customer. Please try again.');
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) =>
      apiClient.delete<CustomerSummary>(endpoints.customers.delete(uuid)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
    onError: () => {
      toast.error('Failed to delete customer. Please try again.');
    },
  });
}

export function useRestoreCustomer() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) =>
      apiClient.post<CustomerSummary>(endpoints.customers.restore(uuid)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
    onError: () => {
      toast.error('Failed to restore customer. Please try again.');
    },
  });
}

export function useMergeCustomers() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: MergeCustomersInput) =>
      apiClient.post<CustomerSummary>(endpoints.customers.merge, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast.success('Customers merged successfully.');
    },
    onError: () => {
      toast.error('Failed to merge customers. Please try again.');
    },
  });
}
